import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Power, PowerOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import PageShell from "@/components/masters/PageShell";
import { useAuth } from "@/context/AuthContext";
import {
  Field,
  GhostButton,
  OutlineButton,
  PrimaryButton,
  SearchBox,
  StatusBadge,
  inputCls,
  selectCls,
} from "@/components/masters/UI";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const empty = { location_id: "", code: "", name: "", status: "active" };

const JobRolesPage = () => {
  const { user, isLocationAdmin, selectedLocationId } = useAuth();
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [q, setQ] = useState("");
  const [locFilter, setLocFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (locFilter && !isLocationAdmin) params.location_id = locFilter;
      const promises = [api.get("/job-roles", { params })];
      if (!isLocationAdmin) {
        promises.push(api.get("/locations", { params: { only_active: true } }));
      }
      const [rolesRes, locsRes] = await Promise.all(promises);
      setRows(rolesRes.data);
      if (!isLocationAdmin && locsRes) {
        setLocations(locsRes.data);
      } else if (user?.location_id) {
        setLocations([{ id: user.location_id, name: user.location_name || "Assigned Location" }]);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, locFilter, selectedLocationId, isLocationAdmin, user]);

  useEffect(() => {
    load();
  }, [load]);

  const locName = useMemo(() => {
    const m = new Map(locations.map((l) => [l.id, l.name]));
    return (id) => m.get(id) || user?.location_name || "—";
  }, [locations, user]);

  const startNew = () => {
    setEditing(null);
    setForm({
      ...empty,
      location_id: isLocationAdmin ? "" : (selectedLocationId || locations[0]?.id || ""),
    });
    setErr(null);
    setOpen(true);
  };

  const startEdit = (row) => {
    setEditing(row);
    setForm({
      location_id: isLocationAdmin ? "" : row.location_id,
      code: row.code,
      name: row.name,
      status: row.status,
    });
    setErr(null);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!isLocationAdmin && !form.location_id) return setErr("Location is required.");
    if (!form.code.trim() || !form.name.trim()) return setErr("Code and Name are required.");

    setSaving(true);
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        status: form.status,
      };
      if (!isLocationAdmin) {
        body.location_id = form.location_id;
      }
      if (editing) {
        await api.put(`/job-roles/${editing.id}`, body);
        toast.success("Job Role updated");
      } else {
        await api.post("/job-roles", body);
        toast.success("Job Role created");
      }
      setOpen(false);
      load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    const next = row.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/job-roles/${row.id}/status`, { status: next });
      toast.success(next === "active" ? "Activated" : "Inactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-vision-ice" data-testid="job-roles-page">
      <AppHeader />
      <PageShell
        eyebrow="Masters · Job Roles"
        title="Job Roles"
        subtitle="Operational designations and assignments per Location. Codes and names are unique within a Location."
        primaryAction={
          <PrimaryButton testId="job-role-add" onClick={startNew}>
            <Plus className="mr-1 inline h-4 w-4" /> Add Job Role
          </PrimaryButton>
        }
        filters={
          <>
            <SearchBox
              value={q}
              onChange={setQ}
              placeholder="Search Code or Name…"
              testId="job-role-search"
            />
            {!isLocationAdmin && (
              <select
                className={selectCls + " w-56"}
                data-testid="job-role-filter-location"
                value={locFilter}
                onChange={(e) => setLocFilter(e.target.value)}
                aria-label="Filter by Location"
              >
                <option value="">All locations</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            )}
          </>
        }
        testId="job-roles-shell"
      >
        <div
          className="overflow-hidden rounded-2xl border bg-white shadow-card"
          style={{ borderColor: "rgba(52,73,94,0.08)" }}
        >
          <div className="hidden grid-cols-[160px_1fr_1.5fr_120px_140px] gap-4 bg-[color:var(--vision-ice)] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60 md:grid">
            <div>Code</div>
            <div>Name</div>
            <div>Location</div>
            <div>Status</div>
            <div className="text-right">Actions</div>
          </div>

          {loading && (
            <div
              className="px-5 py-10 text-center text-sm text-vision-slate/60"
              data-testid="job-roles-loading"
            >
              Loading…
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div
              className="px-5 py-14 text-center text-sm text-vision-slate/60"
              data-testid="job-roles-empty"
            >
              No job roles found.
            </div>
          )}

          {!loading &&
            rows.map((row) => (
              <div
                key={row.id}
                data-testid={`job-role-row-${row.id}`}
                className="grid grid-cols-1 items-center gap-3 border-t px-5 py-4 md:grid-cols-[160px_1fr_1.5fr_120px_140px] md:gap-4"
                style={{ borderColor: "rgba(52,73,94,0.08)" }}
              >
                <div className="font-display text-sm font-bold text-vision-ink">
                  {row.code}
                </div>
                <div className="text-sm font-semibold text-vision-ink">{row.name}</div>
                <div className="text-sm text-vision-slate">{locName(row.location_id)}</div>
                <div>
                  <StatusBadge active={row.status === "active"} />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <GhostButton
                    testId={`job-role-edit-${row.id}`}
                    onClick={() => startEdit(row)}
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </GhostButton>
                  <GhostButton
                    testId={`job-role-toggle-${row.id}`}
                    onClick={() => toggleStatus(row)}
                  >
                    {row.status === "active" ? (
                      <>
                        <PowerOff className="h-3.5 w-3.5" /> Inactivate
                      </>
                    ) : (
                      <>
                        <Power className="h-3.5 w-3.5" /> Activate
                      </>
                    )}
                  </GhostButton>
                </div>
              </div>
            ))}
        </div>
      </PageShell>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="job-role-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-vision-ink">
              {editing ? "Edit Job Role" : "Add Job Role"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={submit} className="mt-2 grid gap-4">
            <Field label="Location">
              {isLocationAdmin ? (
                <input
                  className={inputCls}
                  disabled
                  value={user?.location_name || "Assigned Location"}
                />
              ) : (
                <select
                  className={selectCls}
                  data-testid="job-role-form-location"
                  value={form.location_id}
                  onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                >
                  <option value="">Select a location…</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            <Field label="Code" hint="Unique within the Location, e.g. PCA, LINEN.">
              <input
                className={inputCls}
                data-testid="job-role-form-code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="e.g. PCA"
              />
            </Field>

            <Field label="Name" hint="Descriptive designation title.">
              <input
                className={inputCls}
                data-testid="job-role-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Patient Care Assistant"
              />
            </Field>

            <Field label="Status">
              <select
                className={selectCls}
                data-testid="job-role-form-status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>

            {err && (
              <div
                data-testid="job-role-form-error"
                className="rounded-xl border px-4 py-3 text-sm"
                style={{
                  backgroundColor: "rgba(231, 76, 60, 0.08)",
                  borderColor: "rgba(231, 76, 60, 0.3)",
                  color: "var(--vision-error)",
                }}
              >
                {err}
              </div>
            )}

            <DialogFooter className="mt-2 gap-2 sm:justify-end">
              <OutlineButton
                type="button"
                testId="job-role-form-cancel"
                onClick={() => setOpen(false)}
              >
                Cancel
              </OutlineButton>
              <PrimaryButton
                type="submit"
                testId="job-role-form-submit"
                disabled={saving}
              >
                {saving ? "Saving…" : editing ? "Save changes" : "Create"}
              </PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobRolesPage;
